/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module CoreModule
 */

import { ADMINISTRATION_LICENSING } from 'state-names';
import { AppLevelAlertsService } from 'ng/modules/core/services/app-level-alerts.service';
import { ControllerBannerAlertSeverity } from 'items/controller-banner-alert.item.factory';
import { IFaultItem } from 'ng/modules/collections/factories/controller-fault.collection.factory';
import { Inject, Injectable } from '@angular/core';
import { L10nService } from '@vmw/ngx-vip';
import { StateService } from '@uirouter/core';

import {
    ControllerBannerAlertCollection,
    IBannerAlertMessage,
    TControllerBannerAlertCollection,
} from 'collections/controller-banner-alert.collection.factory';

import * as l10n from './controller-faults.service.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const CONTROLLER_ALERTS_ERROR_ID = 'controller-alerts-error';
const CONTROLLER_ALERTS_INFO_ID = 'controller-alerts-info';
const CONTROLLER_ALERTS_WARN_ID = 'controller-alerts-warn';
const CONTROLLER_BANNER_ALERTS_LOCAL_STORAGE_KEY = 'controller-banner-alerts';
const LICENSE_SERVICE_TYPE = 'license';

interface IFaultItemWithAlertId extends IFaultItem {
    alertId: string;
    service?: string;
}

/**
 * Map severity to alert status for UI display.
 */
const SEVERITY_TO_STATUS_MAP: Record<ControllerBannerAlertSeverity, string> = {
    [ControllerBannerAlertSeverity.ERROR]: 'danger',
    [ControllerBannerAlertSeverity.WARN]: 'warning',
    [ControllerBannerAlertSeverity.INFO]: 'info',
};

/**
 * Map severity to alert IDs.
 */
const SEVERITY_TO_ALERT_ID_MAP: Record<ControllerBannerAlertSeverity, string> = {
    [ControllerBannerAlertSeverity.ERROR]: CONTROLLER_ALERTS_ERROR_ID,
    [ControllerBannerAlertSeverity.WARN]: CONTROLLER_ALERTS_WARN_ID,
    [ControllerBannerAlertSeverity.INFO]: CONTROLLER_ALERTS_INFO_ID,
};

/**
 * Severity order for displaying alerts (Error -> Warn -> Info).
 */
const SEVERITY_ORDER: ControllerBannerAlertSeverity[] = [
    ControllerBannerAlertSeverity.ERROR,
    ControllerBannerAlertSeverity.WARN,
    ControllerBannerAlertSeverity.INFO,
];

/**
 * Generate a unique ID for an alert message using the same logic as getIdFromData_ in item factory.
 */
function getAlertMessageId(alert: IBannerAlertMessage): string {
    const { name = '', description = '' } = alert;

    return `${name}-${description}`;
}

/**
 * Convert IBannerAlertMessage to IFaultItemWithAlertId format for component compatibility.
 * @param actionLabel - Optional action button label for this message
 * @param onActionClick - Optional action button click handler for this message
 */
function convertToFaultItem(
    alert: IBannerAlertMessage,
    actionLabel?: string,
    onActionClick?: () => void,
): IFaultItemWithAlertId {
    return {
        type: alert?.name,
        message: alert?.description,
        alertId: getAlertMessageId(alert),
        service: alert?.service,
        actionLabel,
        onActionClick,
    };
}

/**
 * @description
 * Service to fetch and show/hide banner alerts.
 * Replaces the legacy ControllerFaultsService with the new /api/inventory/banner API.
 * @author alextsg, Shubham Bindal
 */
@Injectable({
    providedIn: 'root',
})
export class ControllerFaultsService {
    private controllerBannerAlertCollection: ControllerBannerAlertCollection | null = null;
    private readonly dismissedAlertIds: Set<string> = new Set();

    constructor(
        private readonly appLevelAlertsService: AppLevelAlertsService,
        @Inject(ControllerBannerAlertCollection)
        private readonly ControllerBannerAlertCollection: TControllerBannerAlertCollection,
        private readonly l10nService: L10nService,
        @Inject('$state')
        private readonly $state: StateService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Set localStorage.bannerAlerts and poll banner alerts. Called when user is logged in
     * to the application. Called both when the user logs in and when user refreshes the page.
     */
    public start(): void {
        this.stop();

        if (!localStorage.getItem(CONTROLLER_BANNER_ALERTS_LOCAL_STORAGE_KEY)) {
            localStorage.setItem(CONTROLLER_BANNER_ALERTS_LOCAL_STORAGE_KEY, '');
        }

        this.dismissedAlertIds.clear();
        this.pollControllerAlerts();
    }

    /**
     * Destroy controllerBannerAlertCollection to stop polling for data.
     */
    public stop(): void {
        localStorage.removeItem(CONTROLLER_BANNER_ALERTS_LOCAL_STORAGE_KEY);

        if (this.controllerBannerAlertCollection) {
            this.controllerBannerAlertCollection.destroy();
            this.controllerBannerAlertCollection = null;
        }

        this.hideControllerAlerts();
    }

    /**
     * Refresh controller banner alert notifications. Called from the welcome component after submit so
     * that notifications get refreshed.
     */
    public refreshControllerFaultsAlert(): void {
        const cachedAlerts = localStorage.getItem(CONTROLLER_BANNER_ALERTS_LOCAL_STORAGE_KEY);

        // If no controller banner alerts present then no need to fetch data again.
        if (!cachedAlerts) {
            return;
        }

        this.hideControllerAlerts();
        this.pollControllerAlerts();
    }

    /**
     * Poll for controller banner alerts.
     */
    private pollControllerAlerts(): void {
        if (!this.controllerBannerAlertCollection) {
            this.controllerBannerAlertCollection = new this.ControllerBannerAlertCollection({
                bind: {
                    collectionLoadSuccess: this.onCollectionLoadSuccess,
                },
            });
        }

        this.controllerBannerAlertCollection.load();
    }

    /**
     * Handle the ControllerBannerAlertCollection load.
     */
    private onCollectionLoadSuccess = (): void => {
        const { allAlerts } = this.controllerBannerAlertCollection;

        const activeAlerts = allAlerts.filter(alert => {
            const alertId = getAlertMessageId(alert);

            return !this.dismissedAlertIds.has(alertId);
        });

        const sortedAlerts = [...activeAlerts]
            .sort((a, b) => (a?.description ?? '').localeCompare(b?.description ?? ''));

        const alertsString = sortedAlerts
            .map(alert => `${alert?.severity ?? ''}:${alert?.description ?? ''}`)
            .join('|');
        const cachedAlerts = localStorage.getItem(CONTROLLER_BANNER_ALERTS_LOCAL_STORAGE_KEY);

        if (cachedAlerts !== alertsString) {
            this.hideControllerAlerts();

            if (activeAlerts.length > 0) {
                this.showAlertsBySeverity(activeAlerts);
            }

            localStorage.setItem(CONTROLLER_BANNER_ALERTS_LOCAL_STORAGE_KEY, alertsString);
        }
    };

    /**
     * Show alerts grouped by severity level.
     * Each severity group is shown as one alert with multiple messages.
     * All license alerts have "Go To Licensing" action button.
     * @param alerts - Array of alerts to display (already filtered for dismissed ones)
     */
    private showAlertsBySeverity(alerts: IBannerAlertMessage[]): void {
        const alertsBySeverity: Record<ControllerBannerAlertSeverity, IBannerAlertMessage[]> = {
            [ControllerBannerAlertSeverity.ERROR]: [],
            [ControllerBannerAlertSeverity.WARN]: [],
            [ControllerBannerAlertSeverity.INFO]: [],
        };

        alerts.forEach(alert => {
            const severity = alert.severity ?? ControllerBannerAlertSeverity.INFO;

            if (alertsBySeverity[severity]) {
                alertsBySeverity[severity].push(alert);
            }
        });

        SEVERITY_ORDER.forEach(severity => {
            if (alertsBySeverity[severity].length > 0) {
                this.showAlerts(severity, alertsBySeverity[severity]);
            }
        });
    }

    /**
     * Add banner alerts as app-level alerts with appropriate status.
     * Each severity group is shown as one alert with multiple messages.
     */
    private showAlerts(severity: ControllerBannerAlertSeverity, alerts: IBannerAlertMessage[]): void {
        const alertId = SEVERITY_TO_ALERT_ID_MAP[severity];
        const status = SEVERITY_TO_STATUS_MAP[severity];

        const faultItems: IFaultItemWithAlertId[] = alerts.map(alert => {
            if (this.isLicenseServiceAlert(alert)) {
                return convertToFaultItem(
                    alert,
                    this.l10nService.getMessage(l10nKeys.goToLicensingLabel),
                    () => this.redirectToLicensing(),
                );
            }

            return convertToFaultItem(alert);
        });

        if (this.appLevelAlertsService.has(alertId)) {
            this.appLevelAlertsService.remove(alertId);
        }

        this.appLevelAlertsService.add({
            id: alertId,
            componentProps: {
                messages: faultItems,
                status,
                onClose: (message: IFaultItemWithAlertId) => this.handleAlertClose(severity, message),
            },
        });
    }

    /**
     * Update all alerts in the correct severity order.
     */
    private updateAllAlertsInOrder(): void {
        if (!this.controllerBannerAlertCollection) {
            return;
        }

        const { allAlerts } = this.controllerBannerAlertCollection;

        // Filter out dismissed alerts
        const activeAlerts = allAlerts.filter(alert => {
            const alertId = getAlertMessageId(alert);

            return !this.dismissedAlertIds.has(alertId);
        });

        // Remove all existing alerts first
        this.hideControllerAlerts();

        // Re-add all alerts in correct order
        if (activeAlerts.length > 0) {
            this.showAlertsBySeverity(activeAlerts);
        }
    }

    /**
     * Handle individual alert dismissal within a severity group.
     * @param severity - Severity of the alert that was closed
     * @param message - The specific message that was closed (in IFaultItemWithAlertId format)
     */
    private handleAlertClose(severity: ControllerBannerAlertSeverity, message: IFaultItemWithAlertId): void {
        if (!message?.alertId) {
            const alertId = SEVERITY_TO_ALERT_ID_MAP[severity];

            if (this.appLevelAlertsService.has(alertId)) {
                this.appLevelAlertsService.remove(alertId);
            }

            return;
        }

        this.dismissedAlertIds.add(message.alertId);
        this.updateAllAlertsInOrder();
    }

    /**
     * Remove all banner alerts from app-level alerts.
     * Used when stopping the service, refreshing, or updating alerts.
     */
    private hideControllerAlerts(): void {
        Object.values(SEVERITY_TO_ALERT_ID_MAP).forEach(alertId => {
            if (this.appLevelAlertsService.has(alertId)) {
                this.appLevelAlertsService.remove(alertId);
            }
        });
    }

    private redirectToLicensing(): void {
        this.$state.go(ADMINISTRATION_LICENSING);
    }

    private isLicenseServiceAlert(alert: IBannerAlertMessage): boolean {
        return alert?.service?.toLowerCase() === LICENSE_SERVICE_TYPE;
    }
}
